'use client';

import React, { useEffect, useRef, useState } from 'react';

/* ── Floating workstation cards ── */

// Pre-computed stable values to avoid hydration mismatch (no Math.random() in render)
const WAVE_BARS = [
  { height: 52.7, dur: '1.14s' },
  { height: 71.3, dur: '0.92s' },
  { height: 44.8, dur: '1.31s' },
  { height: 88.2, dur: '0.78s' },
  { height: 60.5, dur: '1.05s' },
  { height: 47.1, dur: '0.85s' },
  { height: 75.9, dur: '1.22s' },
  { height: 55.4, dur: '0.97s' },
  { height: 83.6, dur: '1.18s' },
  { height: 41.2, dur: '0.73s' },
  { height: 67.8, dur: '1.08s' },
  { height: 79.3, dur: '0.88s' },
  { height: 50.6, dur: '1.35s' },
  { height: 63.1, dur: '0.95s' },
  { height: 86.4, dur: '1.15s' },
  { height: 45.7, dur: '0.82s' },
  { height: 72.9, dur: '1.28s' },
  { height: 58.3, dur: '1.01s' },
];

const LUMA_BARS = [
  30, 38, 42, 35, 28, 33, 40, 45, 38, 32,
  29, 36, 43, 39, 31, 27, 34, 41, 44, 37,
  30, 28, 35, 42, 38, 33, 40, 36, 29, 32,
  38, 41,
];

// Pre-computed stable opacity values: 0.3 + Math.sin(i * 0.3) * 0.2 for i = 0..31
const LUMA_OPACITIES = LUMA_BARS.map((_, i) =>
  parseFloat((0.3 + Math.sin(i * 0.3) * 0.2).toFixed(4))
);

function TimelineCard() {
  return (
    <div
      className="workstation-card float-a rounded-2xl p-4 w-64 shadow-card"
      style={{ position: 'absolute', top: '8%', right: '-2%', zIndex: 20 }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: 'rgba(244,246,248,0.5)' }}>
          Timeline — Seq 03
        </span>
        <span className="text-xs font-mono" style={{ color: '#00E676' }}>01:22:47:08</span>
      </div>
      {/* Track rows */}
      {['V3', 'V2', 'V1', 'A1', 'A2'].map((track, i) => (
        <div key={track} className="flex items-center gap-2 mb-1.5">
          <span className="text-xs w-5" style={{ color: 'rgba(244,246,248,0.35)' }}>{track}</span>
          <div className="flex-1 h-3 rounded-sm overflow-hidden" style={{ background: 'rgba(244,246,248,0.06)' }}>
            {i < 3 && (
              <div
                className="h-full rounded-sm"
                style={{
                  width: `${[72, 45, 88, 30, 55][i]}%`,
                  marginLeft: `${[0, 15, 5, 0, 20][i]}%`,
                  background: i < 3
                    ? `rgba(0,230,118,${[0.7, 0.5, 0.6][i]})`
                    : `rgba(78,95,115,0.8)`,
                }}
              />
            )}
            {i >= 3 && (
              <div className="h-full flex items-center gap-0.5 px-1">
                {WAVE_BARS.map((bar, j) => (
                  <div
                    key={j}
                    className="wave-bar flex-1 rounded-full"
                    style={{
                      height: `${bar.height}%`,
                      background: 'rgba(78,95,115,0.7)',
                      '--dur': bar.dur,
                      animationDelay: `${j * 0.05}s`,
                    } as React.CSSProperties}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
      {/* Playhead */}
      <div className="timeline-track mt-2">
        <div className="timeline-playhead" style={{ width: '38%' }} />
        <div className="scanline" />
      </div>
    </div>
  );
}

function ColorGradeCard() {
  return (
    <div
      className="workstation-card float-c rounded-2xl p-4 w-56 shadow-card"
      style={{ position: 'absolute', bottom: '18%', right: '4%', zIndex: 20 }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: 'rgba(244,246,248,0.5)' }}>
          Color Grade
        </span>
        <span className="w-2 h-2 rounded-full blink" style={{ background: '#00E676' }} />
      </div>
      {/* Scopes */}
      <div className="grid grid-cols-3 gap-1.5 mb-3">
        {[
          { label: 'Lift', val: -0.08, color: '#5B7FE8' },
          { label: 'Gamma', val: 0.12, color: '#00E676' },
          { label: 'Gain', val: 0.24, color: '#E87A5B' },
        ].map((s) => (
          <div key={s.label} className="flex flex-col items-center">
            <div
              className="w-10 h-10 rounded-full border-2 flex items-center justify-center mb-1"
              style={{ borderColor: s.color, background: `${s.color}15` }}
            >
              <span className="text-xs font-mono" style={{ color: s.color }}>
                {s.val > 0 ? '+' : ''}{s.val.toFixed(2)}
              </span>
            </div>
            <span className="text-xs" style={{ color: 'rgba(244,246,248,0.4)' }}>{s.label}</span>
          </div>
        ))}
      </div>
      {/* Luma waveform */}
      <div className="h-10 flex items-end gap-0.5 px-1 rounded overflow-hidden" style={{ background: 'rgba(244,246,248,0.04)' }}>
        {LUMA_BARS.map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-t"
            style={{
              height: `${h}px`,
              background: `rgba(0,230,118,${LUMA_OPACITIES[i]})`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function SoundCard() {
  return (
    <div
      className="workstation-card float-b rounded-2xl p-4 w-52 shadow-card"
      style={{ position: 'absolute', bottom: '28%', left: '-4%', zIndex: 20 }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: 'rgba(244,246,248,0.5)' }}>
          Mix Session
        </span>
        <span className="text-xs font-mono" style={{ color: '#00E676' }}>–0.3 dB</span>
      </div>
      {/* Faders */}
      {[
        { ch: 'Dial', val: 78 },
        { ch: 'Atmo', val: 42 },
        { ch: 'SFX', val: 61 },
        { ch: 'Mx', val: 89 },
      ].map((fader) => (
        <div key={fader.ch} className="flex items-center gap-2 mb-2">
          <span className="text-xs w-8" style={{ color: 'rgba(244,246,248,0.4)' }}>{fader.ch}</span>
          <div className="flex-1 h-1.5 rounded-full" style={{ background: 'rgba(244,246,248,0.08)' }}>
            <div
              className="h-full rounded-full"
              style={{
                width: `${fader.val}%`,
                background: fader.val > 80
                  ? 'linear-gradient(90deg, #00E676, #FFD600)'
                  : '#00E676',
              }}
            />
          </div>
          <span className="text-xs font-mono w-6 text-right" style={{ color: 'rgba(244,246,248,0.4)' }}>
            {fader.val}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function HeroSection() {
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const [showHeadline, setShowHeadline] = useState(false);
  const [showHeroImage, setShowHeroImage] = useState(false);

  // Intentionally worsen LCP: block main thread during initial render
  if (typeof window !== 'undefined') {
    const start = performance.now();
    while (performance.now() - start < 450) {
      // Busy loop
    }
  }

  useEffect(() => {
    // Intentionally worsen LCP: delay rendering of headline
    const t = window.setTimeout(() => setShowHeadline(true), 5200);
    // Intentionally worsen LCP: delay the actual LCP element (hero image)
    const imgT = window.setTimeout(() => setShowHeroImage(true), 5000);

    // Cursor parallax on floating cards
    const handleMouseMove = (e: MouseEvent) => {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      const dx = (e.clientX - cx) / cx;
      const dy = (e.clientY - cy) / cy;

      document.querySelectorAll<HTMLElement>('.float-a').forEach((el) => {
        el.style.marginLeft = `${dx * -8}px`;
        el.style.marginTop = `${dy * -5}px`;
      });
      document.querySelectorAll<HTMLElement>('.float-b').forEach((el) => {
        el.style.marginLeft = `${dx * 6}px`;
        el.style.marginTop = `${dy * 4}px`;
      });
      document.querySelectorAll<HTMLElement>('.float-c').forEach((el) => {
        el.style.marginLeft = `${dx * -5}px`;
        el.style.marginTop = `${dy * 6}px`;
      });
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(imgT);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden grain-overlay"
      style={{
        background: 'linear-gradient(135deg, #060F1C 0%, #0B1A2E 50%, #0F2240 100%)',
        paddingTop: '80px',
      }}
    >
      {/* Atmospheric background blobs */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '-10%',
          right: '-5%',
          width: '60vw',
          height: '60vw',
          background: 'radial-gradient(ellipse, rgba(0,230,118,0.04) 0%, transparent 65%)',
          zIndex: 0,
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: '-15%',
          left: '-10%',
          width: '50vw',
          height: '50vw',
          background: 'radial-gradient(ellipse, rgba(58,74,92,0.3) 0%, transparent 65%)',
          zIndex: 0,
        }}
      />

      <div className="max-w-7xl mx-auto px-6 w-full relative z-10 py-20">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* ── LEFT COLUMN ── */}
          <div>
            {/* Status badge */}
            <div
              className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full mb-8 text-xs font-semibold tracking-widest uppercase"
              style={{
                background: 'rgba(0,230,118,0.08)',
                border: '1px solid rgba(0,230,118,0.2)',
                color: '#00E676',
              }}
            >
              <span className="blink w-1.5 h-1.5 rounded-full" style={{ background: '#00E676' }} />
              London · Cohort 12 Now Enrolling
            </div>

            {/* Headline */}
            {/* Intentionally worsen LCP: big eager-loading hero image */}
            {showHeroImage ? (
              <img
                src="https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?auto=compress&cs=tinysrgb&w=6000"
                alt="Heavy hero"
                loading="eager"
                decoding="sync"
                style={{
                  width: '100%',
                  height: '640px',
                  objectFit: 'cover',
                  borderRadius: '18px',
                  marginBottom: '24px',
                  filter: 'contrast(1.05) saturate(1.1)',
                }}
              />
            ) : (
              <div
                aria-hidden="true"
                style={{
                  width: '100%',
                  height: '640px',
                  marginBottom: '24px',
                  borderRadius: '18px',
                  background: 'rgba(244,246,248,0.06)',
                }}
              />
            )}

            {showHeadline ? (
              <h1
                ref={headlineRef}
                className="font-display font-light leading-none tracking-tight mb-6"
                style={{
                  fontSize: 'clamp(3rem, 7vw, 5.5rem)',
                  color: '#F4F6F8',
                  lineHeight: 0.95,
                }}
              >
                The tools are{' '}
                <span style={{ fontStyle: 'italic', color: '#F4F6F8' }}>already</span>
                <br />
                <span
                  style={{
                    background: 'linear-gradient(90deg, #00E676 0%, #00BFA5 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  open.
                </span>
              </h1>
            ) : (
              <div
                aria-hidden="true"
                style={{
                  height: 'clamp(3rem, 7vw, 5.5rem)',
                  marginBottom: '24px',
                }}
              />
            )}

            {/* Sub */}
            <p
              className="text-lg font-light leading-relaxed mb-10 max-w-md"
              style={{ color: 'rgba(244,246,248,0.6)' }}
            >
              Intensive bootcamps for working creatives. Editing suites, colour bays, sound stages — 
              configured, calibrated, and waiting. Return to your agency sharper than you left.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4 mb-12">
              <a
                href="#signup"
                className="btn-magnetic pulse-green inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full text-sm font-semibold"
                style={{ background: '#00E676', color: '#0B1A2E' }}
              >
                <span className="blink w-2 h-2 rounded-full" style={{ background: '#0B1A2E', opacity: 0.7 }} />
                Start Your Free Module
              </a>
              <a
                href="#bootcamps"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium transition-all duration-200"
                style={{
                  border: '1px solid rgba(244,246,248,0.15)',
                  color: 'rgba(244,246,248,0.75)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(244,246,248,0.35)';
                  (e.currentTarget as HTMLElement).style.color = '#F4F6F8';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(244,246,248,0.15)';
                  (e.currentTarget as HTMLElement).style.color = 'rgba(244,246,248,0.75)';
                }}
              >
                View Bootcamps
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>

            {/* Social proof strip */}
            <div
              className="flex items-center gap-6 pt-8"
              style={{ borderTop: '1px solid rgba(244,246,248,0.08)' }}
            >
              <div className="flex -space-x-2.5">
                {[
                  'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=80',
                  'https://images.pexels.com/photos/733872/pexels-photo-733872.jpeg?auto=compress&cs=tinysrgb&w=80',
                  'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=80',
                  'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=80',
                ].map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`Alumni ${i + 1}`}
                    className="w-9 h-9 rounded-full object-cover"
                    style={{ border: '2px solid #0B1A2E' }}
                  />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 mb-0.5">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <svg key={s} width="12" height="12" viewBox="0 0 24 24" fill="#00E676">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
                <span className="text-xs" style={{ color: 'rgba(244,246,248,0.5)' }}>
                  Trusted by <strong style={{ color: '#F4F6F8' }}>840+ creatives</strong> across the UK
                </span>
              </div>
            </div>
          </div>

          {/* ── RIGHT COLUMN: Floating workstation cards ── */}
          <div className="relative hidden lg:block" style={{ height: '560px' }}>
            {/* Central glow */}
            <div
              className="absolute"
              style={{
                top: '20%',
                left: '15%',
                width: '70%',
                height: '60%',
                background: 'radial-gradient(ellipse, rgba(0,230,118,0.06) 0%, transparent 70%)',
                pointerEvents: 'none',
              }}
            />
            <TimelineCard />
            <SoundCard />
            <ColorGradeCard />

            {/* Central monitor frame */}
            <div
              className="absolute rounded-3xl overflow-hidden"
              style={{
                top: '15%',
                left: '8%',
                right: '8%',
                bottom: '15%',
                background: 'linear-gradient(135deg, rgba(58,74,92,0.4) 0%, rgba(15,34,64,0.6) 100%)',
                border: '1px solid rgba(244,246,248,0.08)',
                zIndex: 10,
              }}
            >
              {/* Monitor header bar */}
              <div
                className="flex items-center gap-2 px-4 py-3"
                style={{ borderBottom: '1px solid rgba(244,246,248,0.06)', background: 'rgba(6,15,28,0.5)' }}
              >
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#FF5F57' }} />
                <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#FEBC2E' }} />
                <div className="w-2.5 h-2.5 rounded-full blink" style={{ background: '#00E676' }} />
                <span className="text-xs ml-3 font-mono" style={{ color: 'rgba(244,246,248,0.3)' }}>
                  upskill_premiere_seq03.prproj
                </span>
              </div>
              {/* "Screen" content */}
              <div className="flex items-center justify-center h-full pb-12">
                <div className="text-center">
                  <div
                    className="text-6xl font-display font-light mb-2"
                    style={{ color: 'rgba(244,246,248,0.06)' }}
                  >
                    RENDER
                  </div>
                  <div
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
                    style={{ background: 'rgba(0,230,118,0.12)', color: '#00E676', border: '1px solid rgba(0,230,118,0.2)' }}
                  >
                    <span className="blink w-1.5 h-1.5 rounded-full" style={{ background: '#00E676' }} />
                    Render Complete — 100%
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom scroll cue */}
      <div
        className="absolute bottom-8 left-1/2 flex flex-col items-center gap-2"
        style={{ transform: 'translateX(-50%)', zIndex: 10 }}
      >
        <span className="text-xs tracking-widest uppercase" style={{ color: 'rgba(244,246,248,0.3)' }}>
          Scroll
        </span>
        <div
          className="w-px h-12 relative overflow-hidden"
          style={{ background: 'rgba(244,246,248,0.1)' }}
        >
          <div className="scanline" />
        </div>
      </div>
    </section>
  );
}